

/**
 * AdminEditOrg page for editing an organization.
 * Uses Shadcn UI Card and Tailwind for layout.
 */
import React from 'react';
import { useParams, useNavigate } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { updateOrgSchema } from '@tyto/shared';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export type OrgFormValues = z.infer<typeof updateOrgSchema>;

export const AdminEditOrg: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch org data
  const {
    data: org,
    isLoading: isOrgLoading,
    error: orgError,
  } = useQuery({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}`);
      if (!res.ok) throw new Error('Failed to fetch organization');
      return res.json();
    },
    enabled: !!orgId,
  });

  // Form
  const form = useForm<OrgFormValues>({
    resolver: zodResolver(updateOrgSchema),
    defaultValues: {
      name: '',
      slug: '',
      telegramChatId: '',
    },
  });

  React.useEffect(() => {
    if (org) {
      form.reset({
        name: org.name,
        slug: org.slug,
        telegramChatId: org.telegramChatId || '',
      });
    }
  }, [org, form]);

  // Update mutation
  const updateOrgMutation = useMutation({
    mutationFn: async (data: OrgFormValues) => {
      const response = await fetch(`/api/org/${orgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update organization');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      navigate(`/app/admin/org/${orgId}`);
    },
  });

  const onSubmit = (data: OrgFormValues) => {
    updateOrgMutation.mutate(data);
  };

  return (
    <div className="max-w-xl mx-auto py-10">
      <div className="mb-6 flex items-center gap-2">
        <a href={`/app/admin/org/${orgId}`} className="text-primary hover:underline flex items-center">
          ← Back to Organizations        </a>
      </div>
      <h1 className="text-3xl font-bold mb-2">Edit Organization</h1>
      {isOrgLoading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : orgError ? (
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          Failed to load organization. Please try again.
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Organization name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input placeholder="organization-slug" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="telegramChatId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telegram Chat ID (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Telegram chat ID" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => navigate('/admin/orgs')}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateOrgMutation.isPending}>
                {updateOrgMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
};
